import React from 'react'
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  useUser,
  useAuth
} from '@clerk/clerk-react'
import { useEffect } from 'react'

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// Syncs the Clerk user to Supabase user_profiles via a server-side API.
// This avoids exposing the service_role key to the browser.
function UserSync() {
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()

  useEffect(() => {
    if (isLoaded && user) {
      syncUser(user, getToken)
    }
  }, [isLoaded, user])

  const syncUser = async (clerkUser, getToken) => {
    try {
      const token = await getToken()
      await fetch('/api/sync-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          clerk_user_id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress,
          full_name: clerkUser.fullName
        })
      })
    } catch (error) {
      console.error('User sync error:', error)
    }
  }

  return null
}

// Auth gate shown when user is not signed in
function AuthGate() {
  // Preserve the current URL (including ?plan= or other query params) so Clerk
  // redirects back to the exact page the user was trying to reach after sign-in.
  const redirectUrl = window.location.pathname + window.location.search || '/app'

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#f7f6f3',
      fontFamily: "'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif"
    }}>
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#2b5ea7', margin: '0 0 8px 0' }}>
          Loyal Sidekick
        </h1>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
          Sign in to access your analytical framework tool.
        </p>
      </div>
      <SignIn
        routing="hash"
        forceRedirectUrl={redirectUrl}
        signUpForceRedirectUrl={redirectUrl}
        fallbackRedirectUrl="/app"
      />
      <a
        href="/"
        style={{
          marginTop: '24px',
          fontSize: '13px',
          color: '#666',
          textDecoration: 'none'
        }}
      >
        Back to Home
      </a>
    </div>
  )
}

export default function ClerkProviderWrapper({ children }) {
  if (!CLERK_PUBLISHABLE_KEY) {
    // Clerk key not available — show a configuration error rather than crashing.
    // App.jsx calls useUser()/useAuth() which require <ClerkProvider> context;
    // rendering children here without a provider throws a React error (blank page).
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f7f6f3',
        fontFamily: "'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif",
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#c5221f', marginBottom: '12px' }}>
          Configuration Error
        </h1>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px', maxWidth: '480px' }}>
          <code>VITE_CLERK_PUBLISHABLE_KEY</code> is not set. Authentication cannot initialise.
        </p>
        <p style={{ fontSize: '13px', color: '#999', maxWidth: '480px' }}>
          Add <strong>VITE_CLERK_PUBLISHABLE_KEY</strong> to your Vercel project&apos;s Environment Variables
          (Settings → Environment Variables), then redeploy.
        </p>
        <a href="/" style={{ marginTop: '24px', fontSize: '13px', color: '#2b5ea7', textDecoration: 'none' }}>
          ← Back to Home
        </a>
      </div>
    )
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      fallbackRedirectUrl="/app"
      signUpFallbackRedirectUrl="/app"
    >
      <SignedIn>
        <UserSync />
        {children}
      </SignedIn>
      <SignedOut>
        <AuthGate />
      </SignedOut>
    </ClerkProvider>
  )
}
